import React from 'react';
import { CsvData, CsvRow } from '../hooks/useCsvData';
import './CsvDataDisplay.css';

interface CsvDataDisplayProps {
  csvData: CsvData | null;
  loading: boolean;
  error: string | null;
  selectedDate: string;
}

const CsvDataDisplay: React.FC<CsvDataDisplayProps> = ({ 
  csvData, 
  loading, 
  error, 
  selectedDate 
}) => {
  if (loading) {
    return (
      <div className="csv-data-display">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Pobieranie danych CSV...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="csv-data-display">
        <div className="error-state">
          <h4>Błąd podczas pobierania danych CSV</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!csvData || csvData.rows.length === 0) {
    return (
      <div className="csv-data-display">
        <div className="no-data">
          <p>Brak danych CSV dla wybranej daty</p>
        </div>
      </div>
    );
  }

  return (
    <div className="csv-data-display">
      <div className="csv-header">
        <h3>Dane CSV z dnia: {formatDate(selectedDate)}</h3>
        <div className="csv-meta">
          <span className="rows-count">{csvData.totalRows} rekordów</span>
          <span className="columns-count">{csvData.headers.length} kolumn</span>
          <span className="constant-count">{csvData.constantColumns.headers.length} stałych</span>
          <span className="variable-count">{csvData.variableColumns.headers.length} zmiennych</span>
        </div>
      </div>
      
      {/* Constant Columns Table */}
      {csvData.constantColumns.headers.length > 0 && (
        <div className="constants-section">
          <h4>📊 Kolumny stałe (identyczne wartości)</h4>
          <div className="constants-table-container">
            <table className="constants-table">
              <thead>
                <tr>
                  <th>Kolumna</th>
                  <th>Wartość</th>
                </tr>
              </thead>
              <tbody>
                {csvData.constantColumns.headers.map((header, index) => (
                  <tr key={index}>
                    <td className="constant-header">
                      <strong>{header}</strong>
                    </td>
                    <td className="constant-value">
                      {csvData.constantColumns.values[header] || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Variable Columns Table */}
      {csvData.variableColumns.headers.length > 0 && (
        <div className="variables-section">
          <h4>📋 Kolumny zmienne (różne wartości)</h4>
          <div className="variables-table-container">
            <table className="variables-table">
              <thead>
                <tr>
                  {csvData.variableColumns.headers.map((header, index) => (
                    <th key={index} title={header}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvData.variableColumns.rows.map((row: CsvRow, index: number) => (
                  <tr key={index}>
                    {csvData.variableColumns.headers.map((header, cellIndex) => (
                      <td key={cellIndex} title={row[header] || ''}>
                        {row[header] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateString;
  }
};

export default CsvDataDisplay;